import {
  CheckCircle, AlertCircle, FileText, ArrowRight,
  Loader2, Mail, ExternalLink, Check, Download, FileCheck,
} from "lucide-react";
import { useSignNow } from "../hooks/useSignNow";

/**
 * SignNowStep
 *
 * Renders the full signing lifecycle for a single document:
 *   • Idle   — doc row with "Sign Now" button
 *   • Loading — spinner while invite is being created
 *   • Ready  — embedded SignNow iframe + fallback options
 *   • Error  — error message with retry
 *   • Signed — green confirmation row with download button
 *
 * Props:
 *   docKey      {string}   Unique key for this document (e.g. "contract")
 *   docLabel    {string}   Human-readable name (e.g. "Independent Contractor Agreement")
 *   templateId  {string}   SignNow template ID
 *   signerEmail {string}
 *   signerName  {string}
 *   onSigned    {function} Called with docKey when signing is confirmed
 *   signed      {boolean}  Controlled — pass true to render the signed state directly
 *   iframeHeight {number}  Optional iframe height in px (default 620)
 */
export default function SignNowStep({
  docKey,
  docLabel,
  templateId,
  signerEmail,
  signerName,
  onSigned,
  signed = false,
  iframeHeight = 620,
}) {
  const { state, signingUrl, errorMsg, initSigning, confirmSigned, retry } = useSignNow({
    templateId,
    signerEmail,
    signerName,
    docKey,
    onSigned,
  });

  // ── Already signed ────────────────────────────────────────────────────────
  if (signed) {
    return (
      <div
        className="settings-doc-row"
        style={{
          background: "#f0fdf4",
          borderRadius: 8,
          padding: "12px 16px",
          marginBottom: 8,
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: 8, flexShrink: 0,
          background: "#dcfce7",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <CheckCircle size={18} color="#16a34a" />
        </div>
        <div style={{ flex: 1 }}>
          <div className="settings-doc-label">{docLabel}</div>
          <div className="settings-doc-status signed">
            ✓ Signed &amp; countersigned — copy sent to {signerEmail}
          </div>
        </div>
        <button className="btn btn-secondary btn-sm">
          <Download size={13} /> Download
        </button>
      </div>
    );
  }

  // ── Idle ──────────────────────────────────────────────────────────────────
  if (state === "idle") {
    return (
      <div
        className="settings-doc-row"
        style={{
          border: "1px solid #fde68a",
          background: "#fffbeb",
          borderRadius: 8,
          padding: "12px 16px",
          marginBottom: 8,
          alignItems: "flex-start",
          gap: 14,
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: 8, flexShrink: 0,
          background: "#fef9c3", marginTop: 2,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <FileText size={18} color="#ca8a04" />
        </div>
        <div style={{ flex: 1 }}>
          <div className="settings-doc-label">{docLabel}</div>
          <div className="settings-doc-status pending">⚠ Signature required</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={initSigning}>
          Sign Now <ArrowRight size={13} />
        </button>
      </div>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (state === "loading") {
    return (
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: 16, background: "#f8fafc", borderRadius: 8, marginBottom: 8,
      }}>
        <Loader2
          size={20}
          color="#2563eb"
          style={{ animation: "spin 1s linear infinite", flexShrink: 0 }}
        />
        <div>
          <div className="settings-doc-label">{docLabel}</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>Preparing your document…</div>
        </div>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (state === "error") {
    return (
      <div style={{
        padding: "14px 16px",
        background: "#fef2f2",
        border: "1px solid #fecaca",
        borderRadius: 8,
        marginBottom: 8,
      }}>
        <div className="settings-error" style={{ marginBottom: 8 }}>
          <AlertCircle size={14} />
          <span>{docLabel}: {errorMsg}</span>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={retry}>
          Retry
        </button>
      </div>
    );
  }

  // ── Ready — iframe ────────────────────────────────────────────────────────
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ marginBottom: 10 }}>
        <div className="settings-doc-label" style={{ fontSize: 14 }}>{docLabel}</div>
        <div style={{ fontSize: 12, color: "#64748b" }}>
          Sign in the window below — we'll detect completion automatically.
        </div>
      </div>

      {/* Signing iframe */}
      <div className="card" style={{ marginBottom: 10, overflow: "hidden" }}>
        <iframe
          src={signingUrl}
          title={`Sign: ${docLabel}`}
          width="100%"
          height={iframeHeight}
          style={{ display: "block", border: "none" }}
          allow="camera; microphone"
        />
      </div>

      {/* Fallback — new tab + manual confirm */}
      <div className="agent-banner">
        <Mail size={16} color="#2563eb" style={{ flexShrink: 0 }} />
        <div className="agent-banner-text">
          <strong>Document not loading?</strong>
          <span>
            <a
              href={signingUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#2563eb", textDecoration: "underline" }}
            >
              Open in a new tab{" "}
              <ExternalLink size={11} style={{ display: "inline", verticalAlign: "middle" }} />
            </a>
          </span>
        </div>
        <button className="btn btn-primary btn-sm" onClick={confirmSigned}>
          <Check size={13} /> I've signed
        </button>
      </div>
    </div>
  );
}